import type { DocumentCategory, DocumentReviewStatus, DateValue } from '../../../../mocks/cases';
import {
  RESIDENCY_DOC_TYPES,
  ASYLUM_DOC_TYPES,
  EMPLOYMENT_DOC_TYPES,
  FAMILY_DOC_TYPES,
  OTHER_DOC_TYPES,
} from '../../../../mocks/cases';
import type { DocState } from './types';

export function getDocTypesForCategory(category: DocumentCategory) {
  switch (category) {
    case 'residence':  return RESIDENCY_DOC_TYPES;
    case 'asylum':     return ASYLUM_DOC_TYPES;
    case 'employment': return EMPLOYMENT_DOC_TYPES;
    case 'family':     return FAMILY_DOC_TYPES;
    case 'other':      return OTHER_DOC_TYPES;
    default:           return [];
  }
}

export function getCategoryDisplayLabel(catKey: DocumentCategory, baseLabel: string, _prorrogaType?: string): string {
  // renewal removed — always return the base label
  return baseLabel;
}

export interface GuidanceCtx { asylumScenario?: string; subRoute?: string; prorrogaType?: string; }

export function getEmptyStateGuidance(catKey: DocumentCategory, ctx: GuidanceCtx): { summary: string; bullets: string[] } {
  switch (catKey) {
    case 'passport':
      return { summary: 'Valid passport or identity document, all pages.', bullets: [
        'Passport photocopy of every page (EX-31 Section 1)',
        'If no passport: Cédula de inscripción, national ID, or travel document plus Anexo I-2 declaration',
      ]};
    case 'residence':
      return { summary: 'Proof of continuous presence in Spain.', bullets: [
        'Empadronamiento (padrón municipal), preferred proof',
        'Medical appointments, school enrollment, utility bills, rental contract',
        'Prior resolutions, invitation letters, transfer receipts',
        ctx.asylumScenario === '1' ? 'Also: proof of 5+ months in Spain since asylum was filed' : '',
      ].filter(Boolean)};
    case 'criminal':
      return { summary: 'One certificate per country of residence in the 5 years before entering Spain. Apostilled or legalized, sworn-translated to Spanish. Valid 3 months.', bullets: [
        'Certificate from country of origin',
        'Plus any other country with >6 months residence in last 5 years',
        'If unavailable after 1 month: upload proof of request; applicant declares on Anexo I-1 inside the EX-31 form',
      ]};
    case 'asylum':
      return { summary: 'Proof of international protection application filed before 1 January 2026.', bullets: [
        'Resguardo de solicitud de protección internacional',
        'Applicant card (tarjeta roja) or admission-to-processing resolution',
      ]};
    case 'employment':
      return { summary: ctx.subRoute === '7A'
        ? 'Proof of work in Spain or a signed job offer to be formalized during processing.'
        : 'Work-related documentation.', bullets: [
        'Work contract, nómina, vida laboral, company certificate',
        'Signed job offer (formalized during provisional permit)',
        'Self-employed: activity description, alta en autónomos',
      ]};
    case 'family':
      return { summary: 'Proof of family unit living together in Spain.', bullets: [
        'Marriage or registered-partnership certificate, libro de familia',
        'Birth certificates for shared children',
        'Joint empadronamiento or cohabitation proof',
        'School enrollment for minors',
      ]};
    case 'vulnerability':
      return { summary: 'Anexo II vulnerability report from public social services or a Third-Sector entity registered in the Immigration Collaborators Registry.', bullets: [
        'Must be signed and sealed',
        'Must specify type of vulnerability',
        'Anexo II is embedded inside EX-32',
      ]};
    default:
      return { summary: 'Additional supporting documents.', bullets: ['Powers of attorney, sworn translations, correspondence, receipts'] };
  }
}

export function isoToDateValue(iso: string): DateValue {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return { day: '', month: '', year: '' };
  const [year, month, day] = iso.slice(0, 10).split('-');
  return { day, month, year };
}

export function dateValueToIso(v: DateValue): string {
  if (!v.year || !v.month || !v.day) return '';
  return `${v.year}-${v.month.padStart(2, '0')}-${v.day.padStart(2, '0')}`;
}

export function sortResidenceDocs(docList: DocState[]): DocState[] {
  return [...docList].sort((a, b) => {
    if (a.docType === 'Padron' && b.docType !== 'Padron') return -1;
    if (a.docType !== 'Padron' && b.docType === 'Padron') return 1;
    return (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? '');
  });
}

export function formatFileExt(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() ?? 'FILE';
}

export const FILE_ICON: Record<string, string> = {
  pdf: 'ri-file-pdf-line', doc: 'ri-file-word-line', docx: 'ri-file-word-line',
  jpg: 'ri-image-line', jpeg: 'ri-image-line', png: 'ri-image-line',
};

export function getFileIcon(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return FILE_ICON[ext] ?? 'ri-file-line';
}

export function getDocTypeLabel(docType?: string): string | null {
  if (!docType) return null;
  return RESIDENCY_DOC_TYPES.find((t) => t.value === docType)?.label ?? docType;
}

export function uploadedCardBorderCls(status: DocumentReviewStatus): string {
  if (status === 'accepted') return 'border-emerald-200 bg-emerald-50';
  if (status === 'rejected') return 'border-rose-200 bg-rose-50';
  return 'border-slate-200 bg-white';
}

const FAKE_SIZES = ['1.2 MB', '2.4 MB', '890 KB', '3.1 MB', '1.7 MB', '560 KB', '4.2 MB', '2.0 MB'];
export function randomSize(): string {
  return FAKE_SIZES[Math.floor(Math.random() * FAKE_SIZES.length)];
}

export function uploadedRowCls(status: DocumentReviewStatus): string {
  if (status === 'accepted') return 'bg-emerald-50';
  if (status === 'rejected') return 'bg-rose-50';
  return 'bg-white';
}

export function reviewStatusLabel(status: DocumentReviewStatus): string {
  if (status === 'accepted') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
}

export function reviewStatusBadgeCls(status: DocumentReviewStatus): string {
  if (status === 'accepted') return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
  if (status === 'rejected') return 'bg-rose-100 text-rose-700 border border-rose-200';
  return 'bg-amber-100 text-amber-700 border border-amber-200';
}

